import { sharedFeedbackValue, isSharedRemoteFeedback, sharedFeedbackText, type SharedRemoteFeedback } from "./sharedFeedbackPresentation";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const remoteMessages = {
  usernameMin: ["count"], usernameMax: ["count"], usernameLowercase: [], usernameStart: [],
  usernamePattern: [], usernameNamedPattern: ["pattern"], passwordMin: ["count"], passwordMax: ["count"],
  passwordCharacters: [], resetUserMissing: [], resetAdminForbidden: [], resetSyncFailed: [],
  resetSystemFailed: ["details"], resetInvalid: [], resetUsed: [], resetExpired: [], validEmailRequired: [],
  tooManyAttempts: [], tokenPasswordRequired: [], invalidRegistration: [], tooManyRegistrations: [],
  usernameExists: [], emailExists: [], accountExists: [], passwordRange: ["min", "max"], usernameNotAllowed: [],
  registerFailed: [],
} as const;
type AuthFailureKey = keyof typeof remoteMessages | "registrationDisabled" | "registrationUnavailable" | "requestFailed" | "resetFailed" | "network";
export type AuthPageFailure = string | SharedRemoteFeedback | { key: AuthFailureKey; values?: Record<string, string | number> };

export function useAuthPages() {
  const { i18n } = useTranslation("auth");
  return useMemo(() => i18n.getFixedT(null, "auth"), [i18n]);
}

export function authPageFailureText(failure: AuthPageFailure | null, t: TFunction<"auth">): string | null {
  if (failure === null || typeof failure === "string") return failure;
  if (isSharedRemoteFeedback(failure)) return sharedFeedbackText(failure);
  const values = failure.key === "resetSystemFailed" && !failure.values?.details
    ? { ...failure.values, details: t($ => $.unknownError) } : failure.values;
  return t(($): string => $[failure.key], values);
}

export function authResponseFailure(presentation: unknown, message: unknown, fallback: AuthFailureKey): AuthPageFailure {
  const shared = sharedFeedbackValue(message, presentation);
  if (shared) return shared;
  if (presentation && typeof presentation === "object" && "code" in presentation && typeof presentation.code === "string"
    && Object.prototype.hasOwnProperty.call(remoteMessages, presentation.code)) {
    const code = presentation.code as keyof typeof remoteMessages;
    const params = "params" in presentation && presentation.params && typeof presentation.params === "object" ? presentation.params : {};
    const values: Record<string, string | number> = {};
    let valid = true;
    for (const name of remoteMessages[code]) {
      const value = Object.prototype.hasOwnProperty.call(params, name) ? (params as Record<string, unknown>)[name] : undefined;
      if (name === "count" || name === "min" || name === "max") {
        if (typeof value !== "number" || !Number.isFinite(value) || value < 0) { valid = false; break; }
      } else if (typeof value !== "string") { valid = false; break; }
      values[name] = value as string | number;
    }
    if (valid) return { key: code, values };
  }
  return typeof message === "string" ? message : { key: fallback };
}

export function registrationFailure(code: unknown, message: unknown, presentation?: unknown): AuthPageFailure {
  if (code === "REGISTRATION_DISABLED" || code === "FEATURE_DISABLED") return { key: "registrationDisabled" };
  if (code === "REGISTRATION_UNAVAILABLE") return { key: "registrationUnavailable" };
  return authResponseFailure(presentation, message, "registerFailed");
}
